import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import {
  LightModeSemanticColors,
  Separator,
  TextButton,
} from '@enechain/ecloud-designsystem'

import { DateNav } from '~/components/balance/FilterCard/DateNav'
import { ToggleSwitch } from '~/components/balance/FilterCard/ToggleSwitch'
import { AreaFilter } from '~/components/common/filters'
import { GENERATION_LIST_SERIES_COLORS } from '~/constants/chartPalette'
import { typography } from '~/constants/typography'
import { useGenerationSourceList } from '~/hooks/useGenerationSourceList'
import { StackOrderModalButton } from '~/pages/BalanceGeneration/components/StackOrderModalButton'
import type { GenSeriesKey } from '~/pages/BalanceGeneration/constants'
import type { UseGenFilterStateReturn } from '~/pages/BalanceGeneration/hooks/useFilterState'

type Props = {
  filter: UseGenFilterStateReturn
}

type ToggleItem = {
  key: GenSeriesKey
  labelKey: string
  /** ON 時のトラック色 = チャートの系列色 (design `.switch-item[data-color]`)。 */
  color: string
}

// 色は [[GENERATION_LIST_SERIES_COLORS]] から引く。
// switch とチャートで別々に色を書くと、 片方だけ変えた時に凡例として嘘になるため
// (需要側 FilterCard と同じ作法)。
const BASIC_TOGGLES: ToggleItem[] = [
  {
    key: 'genPlan',
    labelKey: 'page:balanceGeneration.filter.genPlan',
    color: GENERATION_LIST_SERIES_COLORS.genPlan,
  },
  {
    key: 'genActual',
    labelKey: 'page:balanceGeneration.filter.genActual',
    color: GENERATION_LIST_SERIES_COLORS.genActual,
  },
]

// 発電形式のトグルは並び順・色・ラベルを 9 区分の SoT からそのまま導出する
// (追加定義をここに持たない)。
//
// [#7058 / AC-SPS-007・008] 並び順と色は**個人設定の永続値**なので、module
// スコープの const ではなく hook から取る。 const だと module 読み込み時に
// 既定値で固まり、 設定を変えても凡例が追随しない (= 凡例が嘘になる)。
// チャート (BgChart) と同じ hook を読むので、両者の順・色は常に一致する。
export const FilterCard = ({ filter }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const sourceList = useGenerationSourceList()
  const sourceToggles: ToggleItem[] = useMemo(
    () =>
      sourceList.map((item) => ({
        key: item.key,
        labelKey: item.labelKey,
        color: item.color,
      })),
    [sourceList],
  )

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  }

  const labelStyle: React.CSSProperties = {
    // design balance.css `.filter-card .form-label` = md (14px) / 600 (2026-08-06 実測)。
    // 素の DOM style なので役割 typography の raw CSSProperties を使う。
    ...typography.label,
    width: '104px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  }

  const renderToggle = (item: ToggleItem): React.ReactElement => (
    <ToggleSwitch
      key={item.key}
      isOn={filter.seriesVisibility[item.key]}
      label={t(item.labelKey)}
      onColor={item.color}
      onToggle={() => filter.toggleSeries(item.key)}
    />
  )

  return (
    <div
      style={{
        background: LightModeSemanticColors.Surface.primary.value,
        // design `css/balance.css:10` の `.filter-card { border:1px solid
        // var(--border-low) }`。 実装は 1 段濃い midEmphasis (#d9d9d9) で、
        // 4 画面とも同じズレ方をしていた (issue #7064)。
        border: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '2px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
        <DateNav
          displayDate={filter.displayDate}
          labelKeyPrefix="page:balanceGeneration.filter"
          layout="vertical"
          onDateChange={filter.setDisplayDate}
          onToday={filter.goToToday}
        />

        <Separator orientation="vertical" />

        {/* Right section */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Basic series */}
          <div style={rowStyle}>
            <span style={labelStyle}>
              {t('page:balanceGeneration.filter.basicSeries')}
            </span>
            {BASIC_TOGGLES.map((item) => renderToggle(item))}
          </div>

          {/* Source breakdown */}
          <div style={rowStyle}>
            <span style={labelStyle}>
              {t('page:balanceGeneration.filter.sourceBreakdown')}
            </span>
            {sourceToggles.map((item) => renderToggle(item))}
            {/* [#7058] 「積み上げ順・色」は**この行の右端**に置く (2026-10-02
                デザイナー判断)。行の見出し「発電形式内訳」で何の積み上げ順・色かが
                分かり、トグルと同じ行で種別の見え方を操作する場所にまとまる。
                8080 (`balance/hatsu.html` の `#btn-stack-order`) も同じ行に置く。 */}
            <div style={{ marginLeft: 'auto' }}>
              <StackOrderModalButton />
            </div>
          </div>

          <Separator />

          {/* Area checkboxes (doji-hatsu.md AC-BDH-004/005/012, issue #7536):
              需要側 FilterCard と同じ checkbox マルチセレクト + 全選択/全解除の
              マスターチェック (`IndeterminateCheckboxGroup`)。以前は radio の
              単一選択で「全エリア」が独立した選択肢だったが、AC-BDH-003 NOTE
              (エリアはチェックボックスのマルチセレクト) と需要側との操作方法統一
              のため checkbox へ変更した。選択肢 (`filter.selectableAreaCodes`) は
              呼び出し元 `BalanceGenerationPage` が対象エリア (全 10 エリア) ∩
              業務エリア設定で計算する (issue #7535、conventions.md「業務エリア
              フィルタの原則」)。 */}
          <div style={rowStyle}>
            <span style={{ ...labelStyle, marginBottom: 0 }}>
              {t('page:balanceGeneration.filter.displayArea')}
            </span>
            <AreaFilter
              allLabel={t('page:balanceGeneration.filter.allAreas')}
              areaCodes={filter.selectableAreaCodes}
              onChange={filter.setVisibleAreaCodes}
              value={filter.visibleAreaCodes}
              variant="checkbox"
            />
          </div>
        </div>

        {/* フィルタ群のクリア。 このカードの中で指定したもの (系列トグル /
            エリア) だけを初期値へ戻す。 表示日は戻さない。

            [design差異] 8080 (`balance/hatsu.html` の `#layout-reset-btn`) は
            同じ位置の「クリア」1 つで**フィルタとグラフ配置を両方**戻す。
            2026-09-07 のデザイナー判断で 2 つに分け、グラフ配置は
            チャートの上・右端の「レイアウトリセット」が持つ (需要側と同じ)。 */}
        <TextButton
          flexShrink={0}
          alignSelf="flex-start"
          onClick={filter.clearFilters}
          size="sm"
        >
          {t('page:balanceGeneration.filter.clear')}
        </TextButton>
      </div>
    </div>
  )
}
